import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Crown, Medal, Trophy, Flame, ChevronsDown, LogIn, Sparkles } from "lucide-react";
import { useAuth } from "../components/AuthProvider";

const API = process.env.REACT_APP_BACKEND_URL;

const SCOPES = [
  { id: "alltime", label: "All-Time", icon: Crown },
  { id: "weekly", label: "Weekly", icon: Flame },
];

const CATEGORIES = [
  { id: "all",        label: "All" },
  { id: "music",      label: "Music" },
  { id: "movie",      label: "Movies" },
  { id: "sports",     label: "Sports" },
  { id: "historical", label: "History" },
  { id: "business",   label: "Business" },
];

const RANK_STYLES = {
  1: { ring: "#FFD700", glow: "rgba(255,215,0,0.55)", label: "#FFF3B0", medal: Crown },
  2: { ring: "#C0C7D2", glow: "rgba(192,199,210,0.45)", label: "#EAF0F6", medal: Medal },
  3: { ring: "#E58A4B", glow: "rgba(229,138,75,0.45)", label: "#FFD9B8", medal: Medal },
};

export default function Leaderboard() {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [scope, setScope] = useState("alltime");
  const [category, setCategory] = useState("all");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    fetch(`${API}/api/leaderboard/prime?scope=${scope}&category=${category}&limit=50`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => alive && setData(d))
      .catch(() => alive && setErr("Couldn't load leaderboard."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [scope, category]);

  const rows = data?.rows || [];
  const me = data?.me;

  const jumpToMe = () => {
    const el = document.querySelector("[data-testid='lb-my-row']");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const showJump = useMemo(() => {
    if (!me) return false;
    return rows.some((r) => r.user_id === me.user_id);
  }, [rows, me]);

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-16 pb-nav">
        {/* Header */}
        <button
          data-testid="lb-back-btn"
          onClick={() => nav(-1)}
          className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">
              <Trophy size={12} /> Prime Leaderboard
            </div>
            <h1 className="font-display text-5xl leading-[0.9] tracking-tight mt-1">Top Oracles</h1>
          </div>
          {me && showJump && (
            <button
              data-testid="lb-jump-btn"
              onClick={jumpToMe}
              className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#00E5FF] border border-[#00E5FF]/40 rounded-full px-3 py-1.5 hover:bg-[#00E5FF]/10"
            >
              <ChevronsDown size={12} className="inline mr-1" /> Me
            </button>
          )}
        </div>

        {/* Scope tabs */}
        <div
          data-testid="lb-scope-tabs"
          className="mt-5 rounded-full border border-white/10 bg-black/30 p-1 flex"
        >
          {SCOPES.map((s) => (
            <button
              key={s.id}
              data-testid={`lb-tab-${s.id}`}
              onClick={() => setScope(s.id)}
              className={`flex-1 rounded-full py-2 text-[11px] tracking-[0.2em] uppercase font-bold flex items-center justify-center gap-1.5 transition-colors ${
                scope === s.id ? "bg-[#FF107A] text-[#FDFBF7]" : "text-[#FDFBF7]/60"
              }`}
            >
              <s.icon size={12} /> {s.label}
            </button>
          ))}
        </div>

        {/* Category chips */}
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1" data-testid="lb-cat-chips">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`lb-cat-${c.id}`}
              onClick={() => setCategory(c.id)}
              className={`shrink-0 rounded-full px-3 py-1 text-[10px] tracking-[0.15em] uppercase font-bold transition-colors ${
                category === c.id
                  ? "bg-[#00E5FF] text-[#0A090D]"
                  : "border border-white/15 bg-black/30 text-[#FDFBF7]/60"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Body */}
        {loading && (
          <div className="mt-8 text-center text-[#FDFBF7]/60 text-sm">Loading rankings…</div>
        )}
        {err && !loading && (
          <div className="mt-8 text-center text-[#FF107A] text-sm" data-testid="lb-error">{err}</div>
        )}

        {!loading && !err && rows.length === 0 && (
          <div className="mt-10 rounded-2xl border border-white/10 bg-black/30 p-8 text-center">
            <Sparkles className="mx-auto mb-3 text-[#A855F7]" />
            <div className="font-display text-2xl">Empty week</div>
            <div className="text-sm text-[#FDFBF7]/60 mt-1">No completed challenges yet in this window.</div>
          </div>
        )}

        {/* Podium (top 3) */}
        <AnimatePresence mode="wait">
          {!loading && !err && rows.length >= 3 && (
            <motion.div
              key={scope + "-podium"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid="lb-podium"
              className="mt-6 grid grid-cols-3 gap-2 items-end"
            >
              <PodiumPill user={rows[1]} scope={scope} height="h-24" order={2} />
              <PodiumPill user={rows[0]} scope={scope} height="h-32" order={1} big />
              <PodiumPill user={rows[2]} scope={scope} height="h-20" order={3} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Rest of list */}
        <div className="mt-6 space-y-2" data-testid="lb-rows">
          {rows.slice(3).map((r) => (
            <Row key={r.user_id} row={r} isMe={me && me.user_id === r.user_id} scope={scope} />
          ))}
        </div>

        {/* My rank (when outside visible list) */}
        {me && !showJump && (
          <div className="mt-8">
            <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#00E5FF] mb-2">
              Your Position
            </div>
            <Row row={me} isMe scope={scope} />
          </div>
        )}

        {!user && !loading && (
          <button
            data-testid="lb-signin-btn"
            onClick={login}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FDFBF7] text-[#0A090D] px-6 py-3 font-bold tracking-wide"
          >
            <LogIn size={16} /> Sign in to see your rank
          </button>
        )}

        <div className="mt-6 text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/40 text-center">
          {scope === "weekly" ? "Rolling 7-day window · resets nightly" : "Ranked by Prime Rating"}
        </div>
      </div>
    </div>
  );
}

const PodiumPill = ({ user, scope, height, order, big }) => {
  const style = RANK_STYLES[order] || RANK_STYLES[3];
  const Icon = style.medal;
  return (
    <div className="flex flex-col items-center" data-testid={`lb-podium-${order}`}>
      <div className="relative mb-2">
        <Avatar user={user} size={big ? 64 : 52} ring={style.ring} glow={style.glow} />
        <div
          className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
          style={{ background: style.ring, boxShadow: `0 0 10px ${style.glow}` }}
        >
          <Icon size={12} className="text-[#0A090D]" />
        </div>
      </div>
      <div className="text-center px-1 truncate w-full font-display leading-none" style={{ color: style.label }}>
        <div className="text-sm truncate">{user.name}</div>
      </div>
      <div className="text-[10px] tabular text-[#FDFBF7]/60 mt-0.5">
        {scope === "weekly" ? `${user.weekly_score} pts` : user.prime_rating}
      </div>
      <div
        className={`mt-2 w-full rounded-t-xl ${height} flex items-start justify-center pt-1 font-display text-2xl`}
        style={{
          background: `linear-gradient(180deg, ${style.ring}, transparent 90%)`,
          color: "#0A090D",
          opacity: 0.85,
        }}
      >
        #{order}
      </div>
    </div>
  );
};

const Row = ({ row, isMe, scope }) => {
  const isTop = row.rank <= 3;
  return (
    <div
      data-testid={isMe ? "lb-my-row" : `lb-row-${row.rank}`}
      className={`rounded-2xl border px-3 py-2.5 flex items-center gap-3 ${
        isMe
          ? "border-[#00E5FF]/60 bg-[#00E5FF]/10 shadow-[0_0_18px_rgba(0,229,255,0.35)]"
          : "border-white/10 bg-[#16141D]/80"
      }`}
    >
      <div
        className={`w-8 text-center font-display text-2xl tabular leading-none relative ${
          isTop ? "text-[#FFD700]" : "text-[#FDFBF7]/70"
        }`}
      >
        {row.rank}
        {typeof row.rank_delta === "number" && row.rank_delta !== 0 && (
          <div
            data-testid={`lb-delta-${row.rank}`}
            className="absolute -top-1 -right-1 text-[8px] font-bold px-1 rounded"
            style={{
              background: row.rank_delta > 0 ? "#22C55E" : "#FF107A",
              color: "#0A090D",
              lineHeight: "12px",
            }}
            title={row.rank_delta > 0 ? `Up ${row.rank_delta}` : `Down ${Math.abs(row.rank_delta)}`}
          >
            {row.rank_delta > 0 ? `▲${row.rank_delta}` : `▼${Math.abs(row.rank_delta)}`}
          </div>
        )}
      </div>
      <Avatar user={row} size={40} ring={isMe ? "#00E5FF" : "rgba(255,255,255,0.15)"} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-[#FDFBF7] truncate">{row.name || "Unknown"}</span>
          {isMe && (
            <span className="text-[9px] tracking-[0.2em] uppercase font-bold text-[#00E5FF]">You</span>
          )}
        </div>
        <div className="text-[10px] tracking-[0.15em] uppercase text-[#FDFBF7]/50 truncate">
          {row.title} · {row.accuracy}% acc · {row.prime_matches} primes
        </div>
      </div>
      <div className="text-right">
        <div className="font-display text-xl tabular leading-none text-[#FF107A]">
          {scope === "weekly"
            ? row.weekly_score
            : (row.category_score != null ? row.category_score : row.prime_rating)}
        </div>
        <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/50 mt-0.5">
          {scope === "weekly" ? "pts" : (row.category_score != null ? "pts" : "rating")}
        </div>
      </div>
    </div>
  );
};

const Avatar = ({ user, size = 40, ring = "rgba(255,255,255,0.15)", glow }) => {
  const initial = (user?.name || "?").slice(0, 1).toUpperCase();
  return (
    <div
      className="rounded-2xl overflow-hidden flex items-center justify-center font-display shrink-0"
      style={{
        width: size,
        height: size,
        border: `2px solid ${ring}`,
        background: "#0A090D",
        boxShadow: glow ? `0 0 16px ${glow}` : "none",
      }}
    >
      {user?.picture ? (
        <img src={user.picture} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className="text-[#FF107A]" style={{ fontSize: size * 0.5 }}>{initial}</span>
      )}
    </div>
  );
};
